import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {StaffMemberAbsenceService} from '@services/staff-member-absence.service';
import {selectSchool, StoreState} from '@redux/reducers';
import {StaffMemberAbsence, StaffMemberAbsenceWithDetails} from '@model/staff-member-absence';
import {Observable} from 'rxjs';
import {School} from '@model/school';
import {switchMap, take} from 'rxjs/operators';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {
  staffMemberAbsenceDeleted,
  staffMemberAbsenceLoaded,
  staffMemberAbsenceSaved,
  studiesSaved,
  studyDeleted,
  studyUpdated
} from '@redux/actions/staff-member-absence.action';
import {StaffMember} from '@model/staffMember';
import {DateTime} from 'luxon';
import {Schedule} from '@model/schedule';
import {AttributionTimeEntry} from '@model/attribution-time-entry';
import {Study} from '@model/study';
import {Profile} from '@model/profile';
import {Resource} from '@model/resource';
import {AttributionSummary} from "@model/attribution-summary";

@Injectable()
export class StaffMemberAbsenceFacadeService {

  school$: Observable<School>;
  staffMembers$: Observable<StaffMember[]>;
  staffMemberAbsencesWithDetails$: Observable<StaffMemberAbsenceWithDetails[]>;
  schedule$: Observable<Schedule>;
  attributionsActivesForTeacher$: Observable<AttributionSummary[]>;
  teacherSchedule$: Observable<{[id: number]: {[day: string]: {[time: string]: AttributionTimeEntry}}}>;
  staffMemberAbsenceStudies$: Observable<Study[]>;
  profile$: Observable<Profile>;
  isMobile$: Observable<boolean>;
  resources$: Observable<Resource[]>;

  constructor(
    private _store: Store<StoreState>,
    private _staffMemberAbsenceService: StaffMemberAbsenceService,
    private _sharedFacade: SharedFacadeService,
    ) {
    this.school$ = this._store.pipe(selectSchool);
    this.staffMembers$ = this._sharedFacade.staffMembers$;
    this.staffMemberAbsencesWithDetails$ = this._sharedFacade.staffMemberAbsencesWithDetails$;
    this.schedule$ = this._sharedFacade.schedule$;
    this.attributionsActivesForTeacher$ = this._sharedFacade.attributionsActivesForTeacher$;
    this.teacherSchedule$ = this._sharedFacade.teacherSchedule$;
    this.staffMemberAbsenceStudies$ = this._sharedFacade.staffMemberAbsenceStudies$;
    this.profile$ = this._sharedFacade.profile$;
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.resources$ = this._sharedFacade.resources$;
  }

  loadStaffMemberAbsence(date: DateTime) {
    this._sharedFacade.loadStaffMemberAbsence(date);
  }

  saveStaffMemberAbsence(staffMemberAbsence: StaffMemberAbsence) {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._staffMemberAbsenceService.saveStaffMemberAbsence(school.id, staffMemberAbsence))
    ).subscribe(_staffMemberAbsence => this._store.dispatch(
      staffMemberAbsenceSaved({ staffMemberAbsence })
    ));
  }

  deleteStaffMemberAbsence(staffMemberId: number, date: DateTime) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._staffMemberAbsenceService.deleteStaffMemberAbsence(school.id, staffMemberId, date))
    ).subscribe(() => this._store.dispatch(staffMemberAbsenceDeleted({staffMemberId, date})));
  }

  loadStaffMembers() {
    this._sharedFacade.loadStaffMembers();
  }

  loadSchedule() {
    this._sharedFacade.loadSchedule();
  }

  loadAttributionsActivesForTeacher(staffMemberId: number) {
    this._sharedFacade.loadAttributionsActivesForTeacher(staffMemberId);
  }

  loadScheduleForTeacher(teacherId: number) {
    this._sharedFacade.loadTeacherSchedule(teacherId);
  }

  loadStudiesForStaffMemberAbsence(staffMemberId: number, date: DateTime) {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._staffMemberAbsenceService.loadStaffMemberAbsence(school.id, date))
    ).subscribe(staffMemberAbsences => this._store.dispatch(
      staffMemberAbsenceLoaded({ staffMemberAbsences })
    ));
  }

  saveStudiesForStaffMemberAbsence(staffMemberId: number, date: DateTime, studies: Study[]) {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._staffMemberAbsenceService.saveStudies(school.id, staffMemberId, date, studies))
    ).subscribe(_staffMemberAbsence => this._store.dispatch(
      studiesSaved({ absence: _staffMemberAbsence})
    ));
  }

  loadStaffMemberAbsenceStudy(date: DateTime) {
    this._sharedFacade.loadStaffMemberAbsenceStudy(date);
  }

  deleteStaffMemberAbsenceStudy(staffMemberId: number, date: DateTime, studyId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._staffMemberAbsenceService.deleteStaffMemberAbsenceStudy(school.id, staffMemberId, date, studyId))
    ).subscribe(() => this._store.dispatch(studyDeleted({studyId})));
  }

  updateStudyFromStaffMemberAbsence(date: DateTime, study: Study) {
    this.school$.pipe(
      take(1),
      switchMap(school =>
        this._staffMemberAbsenceService.updateStudyFromStaffMemberAbsence(
          school.id,
          study.absentStaffMemberId,
          date,
          {
            id: study.id,
            substituteId: study.substituteId,
            resourceId: study.resourceId,
            studentsAllowedToLeaveEarly: study.studentsAllowedToLeaveEarly
          }))
    ).subscribe(() => this._store.dispatch(
      studyUpdated({ partialStudy: {
        id: study.id,
        substituteId: study.substituteId,
        substituteFirstName: study.substituteFirstName,
        substituteLastName: study.substituteLastName,
        resourceId: study.resourceId,
        studentsAllowedToLeaveEarly: study.studentsAllowedToLeaveEarly
      }})
    ));
  }

  loadResources() {
    this._sharedFacade.loadResources();
  }
}
